import { t } from "i18next";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { stringifyVideoTimeWithLetters } from "../../../common/video/util/videoTimeStrings";
import PuiTypography from "../../../components/elements/PuiTypography";
import { IsDevice } from "../../../components/function/useDeviceFlag";
import { PuiHighLight, Highlight } from "../../../components/widgets/PuiHighLight";
import { milliSecondToHr } from "../../../utils/ObjectUtils";
import { ShowResult } from "../Search";
import stylesPc from "./ContentSearch.module.css";
import stylesSp from "./ContentSearch_SP.module.css";

/**
|--------------------------------------------------
| サーチ画面詳細情報
|--------------------------------------------------
*/

interface ContentSearchDetailProps {
    content: ShowResult;
    ShowSearchModal: Dispatch<SetStateAction<boolean>>;
    changeOpenDrawerMenu?: Dispatch<SetStateAction<boolean>>;
    selectLanguage: string;
}

export default function ContentSearchDetail({
    content,
    ShowSearchModal,
    changeOpenDrawerMenu,
    selectLanguage,
}: ContentSearchDetailProps) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : stylesSp;

    const history = useHistory();
    const [hitTitle, setHitTitle] = useState<string>("");

    //HitTitleの設定
    useEffect(() => {
        content.hitItem?.forEach((hit) => {
            if (hit.title === "title") {
                setHitTitle(hit.detail);
            }
        });
    }, [content]);

    //video再生画面に遷移
    const gotoVideo = (startMillis: number) => {
        history.push(`/video/${content.id}?time=${stringifyVideoTimeWithLetters(startMillis)}`);
        ShowSearchModal(false);
        if (changeOpenDrawerMenu) changeOpenDrawerMenu(false);
    };

    //slidestock画面に遷移
    const gotoSlideStock = (pageNumber: string) => {
        history.push(`/slidestock/${content.id}?pagenumber=${pageNumber}`);
        ShowSearchModal(false);
        if (changeOpenDrawerMenu) changeOpenDrawerMenu(false);
    };

    return (
        <>
            <div className={styles.root}>
                {/* 画像表示エリア */}
                <div className={styles.leftThumnail}>
                    <Link
                        to={content.isVideo ? `/video/${content.id}` : `/slidestock/${content.id}`}
                        target="_blank"
                        className={styles.imgArea}
                    >
                        <img
                            className={styles.thumbnail}
                            src={content.thumbnails}
                            alt=""
                            onError={(e) =>
                                (e.currentTarget.src =
                                    require("../../../assets/image/NoImage.jpg").default)
                            }
                        />

                        {content.tagList && content.tagList.length > 0 && (
                            <div className={styles.tagArea}>
                                <div className={styles.tagList}>
                                    {content.tagList.map((tag) => (
                                        <div key={tag.id} className={styles.tag}>
                                            {tag.title}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Link>
                </div>

                <div className={styles.thumbnaildiv}>
                    <div className={styles.textBody}>
                        {/* 存在するSTT言語ラベル表示エリア */}
                        <div className={styles.langLabelArea}>
                            {content.lang && (
                                <div className={styles.langTitle}>
                                    <PuiTypography color={"gray-80"} size={12}>
                                        {t("対応言語")}
                                    </PuiTypography>
                                </div>
                            )}
                            {content.lang &&
                                content.lang !== null &&
                                content.lang.length > 0 &&
                                content.lang
                                    .join(",")
                                    .replace("ja", t("日本語"))
                                    .replace("en", "English")
                                    .replace("zh-Hans", t("中文"))
                                    .replace("vi", t("tiếng Việt"))
                                    .split(",")
                                    .map((lang) => (
                                        <div key={lang} className={styles.langName}>
                                            <PuiTypography size={12}>{lang}</PuiTypography>
                                        </div>
                                    ))}
                        </div>
                        {/* タイトル表示 */}
                        <div className={styles.title}>
                            <Link
                                to={
                                    content.isVideo
                                        ? `/video/${content.id}`
                                        : `/slidestock/${content.id}`
                                }
                                target="_blank"
                            >
                                <div className={styles.thumbnailTitle} data-for="title" data-tip="">
                                    <PuiTypography bold={true} size={IsDevice("desktop") ? 24 : 20}>
                                        {content.hitItem.find((hit) => hit.title === "title") ? (
                                            <>
                                                <PuiHighLight
                                                    text={content.title}
                                                    highlights={hitTitle}
                                                />
                                            </>
                                        ) : (
                                            content.title
                                        )}
                                    </PuiTypography>
                                </div>
                            </Link>
                        </div>
                        {/* プレゼンター名表示 */}
                        <div className={styles.presenter}>
                            <div className={styles.personArea}>
                                <div className={styles.presenterImg}>
                                    <img
                                        src={content.presenterImageUrl}
                                        alt={content.presenterImageUrl}
                                        className={styles.presenterIcon}
                                        onError={(e) =>
                                            (e.currentTarget.src =
                                                require("../../../../src/assets/image/noPresenterImage.svg").default)
                                        }
                                    />
                                </div>
                                <PuiTypography color={"secondary-font"} size={17}>
                                    {content.presenterName}
                                </PuiTypography>
                            </div>
                            <div className={styles.viewerCountArea}>
                                <PuiTypography color={"primary-font"} size={14}>
                                    {content.isVideo === true &&
                                        content.viewCount + t("回視聴　 |")}
                                </PuiTypography>
                            </div>
                            <div className={styles.date}>
                                <PuiTypography color={"primary-font"} size={14}>
                                    {window.navigator.language.substring(0, 2) === "ja"
                                        ? content.createDate &&
                                          new Date(content.createDate).toLocaleDateString("Ja-JP", {
                                              year: "numeric",
                                              month: "long",
                                              day: "numeric",
                                          })
                                        : content.createDate &&
                                          new Date(content.createDate).toLocaleDateString("en-US")}
                                </PuiTypography>
                            </div>
                        </div>

                        {content.hitItem.map(
                            (hit, index) =>
                                hit.detail &&
                                hit.detail.length > 0 && (
                                    <div key={index} className={styles.description}>
                                        {/* 詳細表示エリア */}
                                        {/* タイトルが’description'の場合、 */}
                                        {hit.title === "description" && (
                                            <>
                                                {hit.position && (
                                                    <PuiTypography
                                                        size={IsDevice("desktop") ? 16 : 14}
                                                    >
                                                        {hit.position}
                                                    </PuiTypography>
                                                )}
                                                <PuiTypography size={IsDevice("desktop") ? 16 : 14}>
                                                    <Highlight highlights={hit.detail} />
                                                </PuiTypography>
                                            </>
                                        )}
                                        {/* タイトルが’content'の場合、 */}
                                        {hit.title === "content" && (
                                            <div
                                                onClick={() => gotoSlideStock(hit.position)}
                                                className={styles.caption}
                                            >
                                                <PuiTypography
                                                    bold={true}
                                                    size={IsDevice("desktop") ? 14 : 12}
                                                >
                                                    {t("ページ番号：")}
                                                    {hit.position}
                                                </PuiTypography>
                                                <br />
                                                <PuiTypography size={IsDevice("desktop") ? 16 : 14}>
                                                    <Highlight highlights={hit.detail} />
                                                </PuiTypography>
                                            </div>
                                        )}
                                        {/* タイトルが'caption'の場合、 */}
                                        {hit.title === "caption" && hit.lang === selectLanguage && (
                                            <>
                                                <div className={styles.langName}>
                                                    {selectLanguage === "ja" && (
                                                        <PuiTypography size={12}>
                                                            {t("日本語")}
                                                        </PuiTypography>
                                                    )}
                                                    {selectLanguage === "en" && (
                                                        <PuiTypography size={12}>
                                                            English
                                                        </PuiTypography>
                                                    )}
                                                    {selectLanguage === "zh-Hans" && (
                                                        <PuiTypography size={12}>
                                                            {t("中文")}
                                                        </PuiTypography>
                                                    )}
                                                    {selectLanguage === "vi" && (
                                                        <PuiTypography size={12}>
                                                            {t("tiếng Việt")}
                                                        </PuiTypography>
                                                    )}
                                                </div>

                                                <div
                                                    onClick={() => gotoVideo(Number(hit.position))}
                                                    className={styles.caption}
                                                >
                                                    <PuiTypography
                                                        color={"primary-font"}
                                                        size={IsDevice("desktop") ? 14 : 12}
                                                    >
                                                        {milliSecondToHr(Number(hit.position))}
                                                    </PuiTypography>
                                                    <br />
                                                    <PuiTypography
                                                        size={IsDevice("desktop") ? 16 : 14}
                                                    >
                                                        <Highlight highlights={hit.detail} />
                                                    </PuiTypography>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
